
import React, { useState } from "react";
import { X, MapPin, Plus, Check } from "lucide-react";

const AddressSelectionModal = ({
  isOpen,
  onClose,
  addresses = [],
  selectedAddressId,
  onSelectAddress,
  onAddNew,
  loading = false,
}) => {
  const [localSelectedId, setLocalSelectedId] = useState(selectedAddressId);

  const formatAddress = (a) =>
    `${a.address || ""}, ${a.landmark || ""}, ${
      a.city?.name || a.city || ""
    }, ${a.state || ""} - ${a.pincode || ""}`
      .replace(/,\s*,/g, ",")
      .trim();

  const confirm = () => {
    if (!localSelectedId) return alert("Please select an address");
    onSelectAddress(String(localSelectedId));
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="p-5 border-b flex items-center justify-between bg-gray-50">
          <div>
            <h3 className="text-base font-semibold text-gray-800">
              Select Address
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              For home sample collection
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-10">
              <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-blue-600"></div>
              <p className="mt-3 text-sm text-gray-600">
                Loading addresses...
              </p>
            </div>
          ) : addresses.length === 0 ? (
            <div className="text-center py-8">
              <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-700 mb-2">
                No addresses found
              </p>
              <p className="text-xs text-gray-500 mb-5">
                Add one to enable home collection
              </p>
              <button
                onClick={() => {
                  onClose();
                  onAddNew();
                }}
                className="px-5 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto">
                <Plus className="w-3.5 h-3.5" /> Add New Address
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {addresses.map((a) => (
                <div
                  key={a.id}
                  onClick={() => setLocalSelectedId(String(a.id))}
                  className={`p-3.5 border rounded-lg cursor-pointer transition-all text-sm ${
                    String(localSelectedId) === String(a.id)
                      ? "border-blue-500 bg-blue-50 ring-1 ring-blue-200"
                      : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}>
                  <div className="flex items-start gap-3">
                    <div
                      className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        String(localSelectedId) === String(a.id)
                          ? "border-blue-500 bg-blue-500"
                          : "border-gray-300"
                      }`}>
                      {String(localSelectedId) === String(a.id) && (
                        <Check className="w-2.5 h-2.5 text-white" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-800 text-xs">
                          {a.saveas || "Address"}
                        </span>
                        {a.isDefault && (
                          <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">
                            Default
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600">
                        {formatAddress(a)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {addresses.length > 0 && (
          <div className="p-4 border-t bg-gray-50 flex gap-3">
            <button
              onClick={() => {
                onClose();
                onAddNew();
              }}
              className="flex-1 py-2 border border-gray-300 text-gray-700 text-xs rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> New Address
            </button>
            <button
              onClick={confirm}
              disabled={!localSelectedId}
              className="flex-1 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5">
              <Check className="w-3.5 h-3.5" /> Confirm Selection
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressSelectionModal;